import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ABDMIndex from "@/components/ABDMIndex";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { LogOut, Search, RefreshCw, Building2, ShieldCheck, Hospital, ChevronRight,
         Stethoscope, ClipboardList, CalendarClock, Star } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import GovVerificationActions from "@/components/GovVerificationActions";
import GovSchemeBadge from "@/components/GovSchemeBadge";
import FollowupsPanel from "@/components/FollowupsPanel";
import FeedbackPanel from "@/components/FeedbackPanel";

// --------------- Tab A: Government Scheme Verification ---------------
function VerificationQueueTab({ permitted }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.get(`/patients?date=${today}`);
      // Only unverified gov-scheme rows for today
      const pending = data.filter((p) => {
        const m = (p.payment_method || "").toLowerCase();
        return (m === "rghs" || m === "ayushman")
               && !p.gov_verified
               && !p.gov_verification_rejected
               && p.status === "pending_counter_verification";
      });
      setRows(pending);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (permitted) load(); }, [permitted]);

  if (!permitted) {
    return <p className="text-sm text-slate-500 p-6">You don&apos;t have permission for this feature.</p>;
  }

  const filtered = search
    ? rows.filter((r) =>
        (r.name || "").toLowerCase().includes(search.toLowerCase())
        || (r.gov_card_number || "").toLowerCase().includes(search.toLowerCase()))
    : rows;

  return (
    <div className="space-y-4" data-testid="verification-queue-tab">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by patient name or card number"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 className="pl-9" data-testid="verification-search" />
        </div>
        <Button onClick={load} variant="outline" size="icon" data-testid="verification-refresh">
          <RefreshCw size={14} />
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Patient</TableHead>
            <TableHead>Scheme</TableHead>
            <TableHead>Card #</TableHead>
            <TableHead>Mobile</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={5} className="text-center text-slate-400">Loading…</TableCell></TableRow>
          ) : filtered.length === 0 ? (
            <TableRow><TableCell colSpan={5} className="text-center text-slate-400">
              No pending verifications 🎉
            </TableCell></TableRow>
          ) : filtered.map((p) => (
            <TableRow key={p.id} data-testid={`verification-row-${p.id}`}>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <span>{p.name} · {p.age}y</span>
                  <GovSchemeBadge appointment={p} />
                </div>
              </TableCell>
              <TableCell><span className="uppercase text-xs font-mono">{p.payment_method}</span></TableCell>
              <TableCell className="font-mono text-xs">{p.gov_card_number}</TableCell>
              <TableCell>{p.mobile}</TableCell>
              <TableCell className="text-right">
                <GovVerificationActions appointment={p} onDone={load} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// --------------- Tab B: Add Patient ---------------
function AddPatientTab({ permitted }) {
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [mobile, setMobile] = useState("");
  const [gender, setGender] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastToken, setLastToken] = useState(null);

  useEffect(() => {
    if (!permitted) return;
    api.get("/doctors").then(({ data }) => {
      setDoctors(data);
      if (data.length > 0) setDoctorId(data[0].id);
    }).catch(() => { /* ignore */ });
  }, [permitted]);

  if (!permitted) {
    return <p className="text-sm text-slate-500 p-6">You don&apos;t have permission for this feature.</p>;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setLastToken(null);
    try {
      const { data } = await api.post("/patients", {
        doctor_id: doctorId, name, age: Number(age), mobile,
        gender: gender || undefined,
      });
      setLastToken({ id: data.id, ticket: data.ticket_number, name: data.name });
      toast.success(`Token #${data.ticket_number} assigned`);
      setName(""); setAge(""); setMobile(""); setGender("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add patient");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-lg" data-testid="add-patient-tab">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>Doctor</Label>
          <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                  data-testid="add-patient-doctor" required>
            <option value="">Select doctor</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>{d.name} — {d.department}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Patient name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required
                 data-testid="add-patient-name" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Age</Label>
            <Input type="number" min={0} max={120} value={age}
                   onChange={(e) => setAge(e.target.value)} required
                   data-testid="add-patient-age" />
          </div>
          <div>
            <Label>Gender</Label>
            <select value={gender} onChange={(e) => setGender(e.target.value)}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                    data-testid="add-patient-gender">
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div>
          <Label>Mobile</Label>
          <Input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                 maxLength={15} inputMode="numeric" required
                 data-testid="add-patient-mobile" />
        </div>
        <Button type="submit" disabled={busy || !doctorId}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="add-patient-submit">
          {busy ? "Adding…" : "Add to Queue"}
        </Button>
      </form>

      {lastToken && (
        <Card className="p-4 border-emerald-200 bg-emerald-50" data-testid="add-patient-slip">
          <p className="text-xs text-emerald-700 uppercase tracking-wide">Token issued</p>
          <p className="font-heading text-4xl font-bold text-emerald-900">#{lastToken.ticket}</p>
          <p className="text-sm text-emerald-800">{lastToken.name}</p>
          <Button variant="outline" size="sm" onClick={() => window.print()}
                  className="mt-3" data-testid="add-patient-print">Print slip</Button>
        </Card>
      )}
    </div>
  );
}

// --------------- Tab C / D shared: appointments list ---------------
function AppointmentsTab({ permitted, permKey, showTicket = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.get(`/patients?date=${today}`);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (permitted) load(); }, [permitted]);

  if (!permitted) {
    return <p className="text-sm text-slate-500 p-6">You don&apos;t have permission for this feature.</p>;
  }

  return (
    <div className="space-y-3" data-testid={`${permKey}-tab`}>
      <div className="flex justify-end">
        <Button onClick={load} variant="outline" size="sm">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {showTicket && <TableHead>Token</TableHead>}
            <TableHead>Patient</TableHead>
            <TableHead>Mobile</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Scheme</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={showTicket ? 5 : 4} className="text-center text-slate-400">Loading…</TableCell></TableRow>
          ) : rows.length === 0 ? (
            <TableRow><TableCell colSpan={showTicket ? 5 : 4} className="text-center text-slate-400">No appointments today</TableCell></TableRow>
          ) : rows.map((p) => (
            <TableRow key={p.id}>
              {showTicket && <TableCell className="font-mono">{p.ticket_number || "—"}</TableCell>}
              <TableCell>{p.name} · {p.age}y</TableCell>
              <TableCell>{p.mobile}</TableCell>
              <TableCell>
                <Badge variant={p.status === "completed" ? "default" : p.status === "cancelled" ? "destructive" : "outline"}>
                  {p.status}
                </Badge>
              </TableCell>
              <TableCell><GovSchemeBadge appointment={p} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// --------------- Main Dashboard ---------------

// --------------- Tab E: Doctors (read-only list) ---------------
function DoctorsTab({ permitted }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!permitted) return;
    api.get("/doctors").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, [permitted]);
  if (!permitted) return <p className="text-sm text-slate-500 p-6">You don&apos;t have permission for this feature.</p>;
  return (
    <div className="space-y-3" data-testid="recp-doctors-tab">
      <p className="text-sm text-slate-500">{rows.length} doctor(s) at this hospital</p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead><TableHead>Specialization</TableHead>
            <TableHead>Live Status</TableHead><TableHead className="text-right">Today&apos;s Bookings</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={4} className="text-center text-slate-400">Loading…</TableCell></TableRow>
          ) : rows.length === 0 ? (
            <TableRow><TableCell colSpan={4} className="text-center text-slate-400">No doctors</TableCell></TableRow>
          ) : rows.map((d) => (
            <TableRow key={d.id} data-testid={`recp-doctor-row-${d.id}`}>
              <TableCell className="font-medium">{d.name}</TableCell>
              <TableCell className="text-slate-600">{d.specialization_en}</TableCell>
              <TableCell>
                {d.current_ticket
                  ? <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">#{d.current_ticket}</Badge>
                  : <Badge variant="outline">Idle</Badge>}
              </TableCell>
              <TableCell className="text-right font-semibold">{d.today_bookings || 0}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// --------------- Tab F: Reports (visit search) ---------------
function ReportsTab({ permitted }) {
  const [reports, setReports] = useState([]);
  const [date, setDate] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const search = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (date) p.set("date", date);
      if (q) p.set("q", q);
      const { data } = await api.get(`/dashboard/admin/reports?${p}`);
      setReports(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };
  if (!permitted) return <p className="text-sm text-slate-500 p-6">You don&apos;t have permission for this feature.</p>;
  return (
    <div className="space-y-3" data-testid="recp-reports-tab">
      <div className="grid sm:grid-cols-3 gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="recp-report-date" />
        <Input placeholder="Search name or mobile" value={q} onChange={(e) => setQ(e.target.value)}
               className="sm:col-span-2" data-testid="recp-report-q" />
      </div>
      <Button onClick={search} disabled={loading} data-testid="recp-report-search">
        <Search size={14} className="mr-1" /> {loading ? "Searching…" : "Search"}
      </Button>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead><TableHead>Ticket</TableHead>
            <TableHead>Patient</TableHead><TableHead>Doctor</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Fee</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reports.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No results</TableCell></TableRow>
          ) : reports.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="text-xs">{r.date}</TableCell>
              <TableCell className="font-mono">#{r.ticket_number}</TableCell>
              <TableCell>{r.name} <span className="text-xs text-slate-500">· {r.age}</span></TableCell>
              <TableCell className="text-slate-600">{r.doctor_name}</TableCell>
              <TableCell><Badge variant={r.status === "completed" ? "default" : "outline"}>{r.status}</Badge></TableCell>
              <TableCell className="text-right">₹{r.fee}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}


export default function ReceptionistDashboard() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [me, setMe] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadMe = async ({ toast: showToast } = {}) => {
    try {
      const { data } = await api.get("/receptionist/me");
      setMe(data);
      if (showToast) toast.success("Permissions refreshed");
      return data;
    } catch {
      toast.error("Session expired. Please log in again.");
      nav("/receptionist/login");
      return null;
    }
  };

  useEffect(() => { loadMe(); }, []);

  // Poll every 60s so revoked permissions or is_active flips reflect on
  // the dashboard without the user having to manually refresh.
  useEffect(() => {
    if (!me) return undefined;
    const t = setInterval(() => { loadMe(); }, 60_000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  const refreshPermissions = async () => {
    setRefreshing(true);
    try { await loadMe({ toast: true }); }
    finally { setRefreshing(false); }
  };

  const doLogout = async () => {
    try { await api.post("/receptionist/logout"); } catch { /* ignore */ }
    await logout();
    nav("/receptionist/login");
  };

  if (!me) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  const perms = me.permissions || {};
  // Any IPD sub-permission (or the master flag) grants the "Manage IPD" tile
  // and, in turn, filtered access to the /receptionist/ipd page.
  const ipdSubKeys = [
    "can_ipd_wards", "can_ipd_beds", "can_ipd_admissions", "can_ipd_notes",
    "can_ipd_discharge", "can_ipd_billing", "can_ipd_inventory", "can_ipd_reporting",
  ];
  const hasAnyIpdAccess = !!perms.can_manage_ipd || ipdSubKeys.some((k) => !!perms[k]);

  const tabs = [];
  if (perms.can_verify_gov_schemes || perms.can_manage_gov_schemes ||
      perms.can_manage_rghs_scheme || perms.can_manage_ayushman_scheme) {
    tabs.push({ key: "verify", label: "Gov Verification" });
  }
  if (perms.can_add_patients) tabs.push({ key: "add", label: "Add Patient" });
  if (perms.can_view_queue) tabs.push({ key: "queue", label: "Live Queue" });
  if (perms.can_view_appointments || perms.can_view_bookings) tabs.push({ key: "appts", label: "Appointments" });
  if (perms.can_view_doctors || perms.can_view_live_diagnosis) tabs.push({ key: "doctors", label: "Doctors" });
  if (perms.can_view_reports || perms.can_search_patient_visits) tabs.push({ key: "reports", label: "Reports" });
  if (perms.can_manage_followups || perms.can_send_reminders) tabs.push({ key: "followups", label: "Follow-ups" });
  if (perms.can_view_feedback) tabs.push({ key: "feedback", label: "Feedback" });
  tabs.push({ key: "abdm", label: "🏥 ABDM" }); 
  const first = tabs[0]?.key || "empty";
  const gridCols =
    tabs.length <= 2 ? "grid-cols-2"
    : tabs.length === 3 ? "grid-cols-3"
    : tabs.length <= 4 ? "grid-cols-2 md:grid-cols-4"
    : tabs.length <= 8 ? "grid-cols-2 md:grid-cols-4 lg:grid-cols-8"
    : "grid-cols-3 md:grid-cols-5 lg:grid-cols-9";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="receptionist-dashboard">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-full p-2 bg-indigo-100 text-indigo-700">
            <Building2 size={18} />
          </div>
          <div>
            <p className="font-heading text-lg font-semibold text-slate-900">Receptionist Dashboard</p>
            <p className="text-xs text-slate-500">{me.hospital_name || "—"} · {me.full_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refreshPermissions}
                  disabled={refreshing}
                  data-testid="receptionist-refresh-permissions">
            <RefreshCw size={14} className={refreshing ? "mr-1 animate-spin" : "mr-1"} />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={doLogout} data-testid="receptionist-logout">
            <LogOut size={16} className="mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="p-4 md:p-8 max-w-6xl mx-auto space-y-4">
        {hasAnyIpdAccess && (
          <Card
            className="p-5 cursor-pointer hover:-translate-y-0.5 transition-transform border-blue-100 bg-gradient-to-br from-blue-50 via-white to-white"
            onClick={() => nav("/receptionist/ipd")}
            data-testid="receptionist-manage-ipd-card"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") nav("/receptionist/ipd"); }}
          >
            <div className="flex items-center gap-4">
              <div className="rounded-xl p-3 bg-blue-100 text-blue-700">
                <Hospital className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="font-heading text-lg font-semibold text-slate-900">Manage IPD</p>
                <p className="text-sm text-slate-500">Ward management, admissions, discharge, billing &amp; inventory</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </Card>
        )}

        {tabs.length === 0 && !hasAnyIpdAccess ? (
          <Card className="p-8 text-center space-y-2">
            <ShieldCheck className="w-8 h-8 mx-auto text-slate-400" />
            <p className="font-heading text-lg font-semibold text-slate-900">No features enabled</p>
            <p className="text-sm text-slate-500">Your administrator hasn&apos;t granted you any dashboard permissions yet.
              Please contact them.</p>
          </Card>
        ) : tabs.length === 0 ? null : (
          <Tabs defaultValue={first} className="w-full">
            <TabsList className={`grid ${gridCols} mb-4`} data-testid="receptionist-tabs">
              {tabs.map((t) => (
                <TabsTrigger key={t.key} value={t.key} data-testid={`receptionist-tab-${t.key}`}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
            {(perms.can_verify_gov_schemes || perms.can_manage_gov_schemes ||
              perms.can_manage_rghs_scheme || perms.can_manage_ayushman_scheme) && (
              <TabsContent value="verify"><Card className="p-4"><VerificationQueueTab permitted={true} /></Card></TabsContent>
            )}
            {perms.can_add_patients && (
              <TabsContent value="add"><Card className="p-4"><AddPatientTab permitted={true} /></Card></TabsContent>
            )}
            {perms.can_view_queue && (
              <TabsContent value="queue"><Card className="p-4"><AppointmentsTab permitted={true} permKey="queue" showTicket={true} /></Card></TabsContent>
            )}
            {(perms.can_view_appointments || perms.can_view_bookings) && (
              <TabsContent value="appts"><Card className="p-4"><AppointmentsTab permitted={true} permKey="appts" showTicket={true} /></Card></TabsContent>
            )}
            {(perms.can_view_doctors || perms.can_view_live_diagnosis) && (
              <TabsContent value="doctors"><Card className="p-4"><DoctorsTab permitted={true} /></Card></TabsContent>
            )}
            {(perms.can_view_reports || perms.can_search_patient_visits) && (
              <TabsContent value="reports"><Card className="p-4"><ReportsTab permitted={true} /></Card></TabsContent>
            )}
            {(perms.can_manage_followups || perms.can_send_reminders) && (
              <TabsContent value="followups"><Card className="p-4"><FollowupsPanel api={api} /></Card></TabsContent>
            )}
            {perms.can_view_feedback && (
              <TabsContent value="feedback"><Card className="p-4"><FeedbackPanel api={api} /></Card></TabsContent>
            )}
              <TabsContent value="abdm"><Card className="p-4"><ABDMIndex /></Card></TabsContent>
          
          </Tabs>
        )}
      </main>
    </div>
  );
}
